import type { BenchmarkRow, DashboardResponse } from '@ferty9/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { loadChartConstructor } from '../lib/third-party/chart';
import { SelectGroup } from './common';
import type { AdminActions, DashboardExportFilters } from '../types';

type ChartInstance = { destroy: () => void };
type ChartConstructor = new (context: HTMLCanvasElement, config: Record<string, unknown>) => ChartInstance;
type Row = Record<string, unknown>;

const GREEN = '#10b981';
const AMBER = '#f59e0b';
const RED = '#ef4444';
const PRIMARY = '#e91e63';
const TEAL = '#0d9488';

function record(value: unknown): Row {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Row : {};
}

function array(value: unknown): Row[] {
  return Array.isArray(value) ? value.map(record) : [];
}

function text(value: unknown, fallback = '-') {
  return value == null || value === '' ? fallback : String(value);
}

function cleanCenterName(value: unknown) {
  return text(value).replace('Ferty9 Fertility Center - ', '');
}

function pct(value: number, total: number) {
  return total > 0 ? Math.round((value / total) * 100) : 0;
}

function signed(value: number) {
  return value > 0 ? `+${value}` : String(value);
}

function ratingText(value: number) {
  if (!Number.isFinite(value)) return '-';
  return `${value.toFixed(2)}★`;
}

function journeyKey(value: string) {
  if (value.includes('OPU')) return 'OPU';
  if (value.includes('ET') || value.includes('Embryo')) return 'ET';
  return 'NEW_PATIENT';
}

function statNumber(source: Row, keys: string[], fallback = 0) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
  }
  return fallback;
}

function dashboardStats(dashboard: DashboardResponse | null) {
  const root = record(dashboard);
  const stats = { ...root, ...record(root.stats) };
  const distribution = record(root.distribution);
  const promoters = statNumber(stats, ['promoters'], statNumber(distribution, ['promoters']));
  const passives = statNumber(stats, ['passives'], statNumber(distribution, ['passives']));
  const detractors = statNumber(stats, ['detractors'], statNumber(distribution, ['detractors']));
  const total = statNumber(stats, ['total', 'totalResponses', 'responses'], promoters + passives + detractors);
  const nps = statNumber(stats, ['nps', 'npsScore'], total ? Math.round(((promoters - detractors) / total) * 100) : 0);
  const responseRate = statNumber(stats, ['responseRate'], 0);
  return { root, total, nps, promoters, passives, detractors, responseRate };
}

function centerRows(root: Row) {
  return array(root.centerBreakdown ?? root.centers).map(item => {
    const promoters = statNumber(item, ['promoters']);
    const passives = statNumber(item, ['passives']);
    const detractors = statNumber(item, ['detractors']);
    const total = promoters + passives + detractors || statNumber(item, ['total', 'responses']);
    const nps = statNumber(item, ['nps', 'score'], total ? Math.round(((promoters - detractors) / total) * 100) : 0);
    return { ...item, name: cleanCenterName(item.center ?? item.name), promoters, passives, detractors, total, nps };
  });
}

function journeyRows(root: Row) {
  return array(root.journeyDetails ?? root.journeys).map(item => ({
    ...item,
    journey: text(item.journey ?? item.name),
    responses: statNumber(item, ['responses', 'total']),
    nps: statNumber(item, ['nps', 'score']),
    csat: statNumber(item, ['csat', 'avgRating', 'average']),
    avgRating: statNumber(item, ['avgRating', 'average'], statNumber(item, ['csat']) ? statNumber(item, ['csat']) / 20 : 0),
    promoters: statNumber(item, ['promoters']),
    passives: statNumber(item, ['passives']),
    detractors: statNumber(item, ['detractors']),
  }));
}

function staffRows(root: Row) {
  return array(root.staffLeaderboard ?? root.staffPerformance ?? root.staff).map(item => ({
    ...item,
    name: text(item.staff ?? item.name ?? item.feedback_by),
    count: statNumber(item, ['count', 'responses', 'total']),
    nps: statNumber(item, ['nps', 'score']),
  }));
}

function questionCsatRows(root: Row) {
  return array(root.questionWiseCsat ?? root.questionWise ?? root.categories);
}

function categoryRows(root: Row) {
  return array(root.categoryBreakdown).map(item => ({
    ...item,
    name: text(item.name ?? item.category),
    score: statNumber(item, ['avg', 'score', 'avgRating', 'average']),
  }));
}

function trendRows(root: Row) {
  return array(root.trend).map((item, index) => ({
    label: text(item.week ?? item.date ?? item.label, `Point ${index + 1}`),
    nps: statNumber(item, ['avg', 'nps', 'score', 'value']),
    volume: statNumber(item, ['volume', 'responses', 'total', 'count']),
    csat: statNumber(item, ['csat', 'avgRating', 'average']),
  }));
}

function formatLastUpdated(value: Date | null) {
  if (!value) return 'Not yet loaded';
  const date = value.toLocaleDateString([], { day: 'numeric', month: 'short' });
  const time = value.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `Last updated: ${date} at ${time}`;
}

function npsStatus(score: number) {
  if (score >= 70) return { label: 'Excellent', tone: 'success', color: GREEN, dot: '#6ee7b7' };
  if (score >= 40) return { label: 'Strong', tone: 'accent', color: TEAL, dot: '#99f6e4' };
  if (score >= 10) return { label: 'Healthy', tone: 'warning', color: AMBER, dot: '#fcd34d' };
  return { label: 'Critical', tone: 'danger', color: RED, dot: '#fca5a5' };
}

function Icon({ name }: { name: 'refresh' | 'clock' | 'download' | 'activity' | 'users' | 'medal' | 'smile' | 'meh' | 'frown' | 'sparkles' | 'brain' | 'tag' | 'trophy' | 'warning' | 'star' | 'wrench' | 'rocket' }) {
  const common = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (name === 'refresh') return <svg {...common} id="refresh-icon"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" /><path d="M16 16h5v5" /></svg>;
  if (name === 'clock') return <svg {...common} width={13} height={13}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>;
  if (name === 'download') return <svg {...common} width={16} height={16}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>;
  if (name === 'users') return <svg {...common}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
  if (name === 'medal') return <svg {...common}><path d="M7.21 15 2.66 7.14a2 2 0 0 1 .13-2.2L4.4 2.8A2 2 0 0 1 6 2h12a2 2 0 0 1 1.6.8l1.61 2.14a2 2 0 0 1 .13 2.2L16.79 15" /><circle cx="12" cy="17" r="5" /></svg>;
  if (name === 'smile') return <svg {...common}><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>;
  if (name === 'meh') return <svg {...common}><circle cx="12" cy="12" r="10" /><line x1="8" y1="15" x2="16" y2="15" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>;
  if (name === 'frown') return <svg {...common}><circle cx="12" cy="12" r="10" /><path d="M16 16s-1.5-2-4-2-4 2-4 2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>;
  if (name === 'sparkles') return <svg {...common}><path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" /><path d="m5 3 1 2.5L8.5 6 6 7 5 9.5 4 7 1.5 6 4 5.5Z" /></svg>;
  if (name === 'brain') return <svg {...common}><path d="M12 5a3 3 0 1 0-6 .1 4 4 0 0 0-2.5 5.8 4 4 0 0 0 .6 6.6A4 4 0 1 0 12 18Z" /><path d="M12 5a3 3 0 1 1 6 .1 4 4 0 0 1 2.5 5.8 4 4 0 0 1-.6 6.6A4 4 0 1 1 12 18Z" /><path d="M12 5v14" /></svg>;
  if (name === 'tag') return <svg {...common}><path d="M12.6 2.6H2v10.6L11.4 22 22 11.4Z" /><circle cx="7" cy="7" r="1" /></svg>;
  if (name === 'trophy') return <svg {...common}><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.45 1-1 1H4v2h16v-2h-5c-.55 0-1-.45-1-1v-2.34" /><path d="M12 2a6.37 6.37 0 0 1 6 6.37C18 12.37 15.37 14 12 14S6 12.37 6 8.37A6.37 6.37 0 0 1 12 2z" /></svg>;
  if (name === 'warning') return <svg {...common}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>;
  if (name === 'star') return <svg {...common}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>;
  if (name === 'wrench') return <svg {...common}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg>;
  if (name === 'rocket') return <svg {...common}><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" /><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" /><path d="M9 12H4s.55-3.03 2-4a14 14 0 0 1 3-1.85" /><path d="M12 15v5s3.03-.55 4-2a14 14 0 0 0 1.85-3" /></svg>;
  return <svg {...common}><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></svg>;
}

function ChartCanvas({ id, config }: { id: string; config: Record<string, unknown> }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<ChartInstance | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return undefined;
    let disposed = false;
    void loadChartConstructor().then((Chart) => {
      if (disposed || !canvasRef.current) return;
      chartRef.current?.destroy();
      chartRef.current = new (Chart as ChartConstructor)(canvasRef.current, config);
    });
    return () => {
      disposed = true;
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [config]);

  return <canvas id={id} ref={canvasRef} />;
}

export function DashboardTab({
  active,
  centers,
  center,
  days,
  customFromDate,
  customToDate,
  journey,
  dashboard,
  benchmark,
  lastUpdated,
  autoRefresh,
  onCenter,
  onDays,
  onCustomFromDate,
  onCustomToDate,
  onJourney,
  onRefresh,
  onAutoRefreshChange,
  exportDisabled = false,
  actions,
}: {
  active: boolean;
  centers: string[];
  center: string;
  days: string;
  customFromDate: string;
  customToDate: string;
  journey: string;
  dashboard: DashboardResponse | null;
  benchmark: BenchmarkRow[];
  lastUpdated: Date | null;
  autoRefresh: boolean;
  onCenter: (value: string) => void;
  onDays: (value: string) => void;
  onCustomFromDate: (value: string) => void;
  onCustomToDate: (value: string) => void;
  onJourney: (value: string) => void;
  onRefresh: () => void;
  onAutoRefreshChange: (value: boolean) => void;
  exportDisabled?: boolean;
  actions: AdminActions;
}) {
  const view = useMemo(() => buildDashboardView(dashboard, benchmark), [dashboard, benchmark]);
  return (
    <section id="tab-dashboard" className="tab-content" style={{ display: active ? 'block' : 'none' }}>
      <DashboardFilters centers={centers} center={center} days={days} customFromDate={customFromDate} customToDate={customToDate} journey={journey} lastUpdated={lastUpdated} autoRefresh={autoRefresh} exportDisabled={exportDisabled} onCenter={onCenter} onDays={onDays} onCustomFromDate={onCustomFromDate} onCustomToDate={onCustomToDate} onJourney={onJourney} onRefresh={onRefresh} onAutoRefreshChange={onAutoRefreshChange} onExportPdf={(filters) => void actions.exportDashboardPdf(filters)} />
      <div id="dashboard-pdf-content">
        <NpsHeroBanner view={view} />
        <OperationalStrategyAdvisor view={view} />
        <KpiSection view={view} />
        <NpsDistributionBar view={view} />
        <TrendAndDistribution view={view} />
        <ServiceAndJourneyCharts view={view} />
        <NpsByServiceCentre view={view} />
        <CsatSummaryTables view={view} />
        <JourneyDetailedMetrics view={view} onExport={() => void actions.exportJourneyDetailedMetrics()} exportDisabled={exportDisabled} />
        <NlpAnalysis view={view} />
      </div>
    </section>
  );
}

function DashboardFilters({ centers, center, days, customFromDate, customToDate, journey, lastUpdated, autoRefresh, exportDisabled, onCenter, onDays, onCustomFromDate, onCustomToDate, onJourney, onRefresh, onAutoRefreshChange, onExportPdf }: { centers: string[]; center: string; days: string; customFromDate: string; customToDate: string; journey: string; lastUpdated: Date | null; autoRefresh: boolean; exportDisabled: boolean; onCenter: (value: string) => void; onDays: (value: string) => void; onCustomFromDate: (value: string) => void; onCustomToDate: (value: string) => void; onJourney: (value: string) => void; onRefresh: () => void; onAutoRefreshChange: (value: boolean) => void; onExportPdf: (filters: DashboardExportFilters) => void }) {
  const exportCurrentFilters = () => onExportPdf({
    center,
    days,
    customFromDate,
    customToDate,
    journey,
  });

  return (
    <div className="card filter-card dashboard-filter-card">
      <div className="filter-row dashboard-filter-row" style={{ marginBottom: 0 }}>
        <SelectGroup label="Center Location" id="filter-center" value={center} onChange={onCenter} options={['ALL', ...centers]} allLabel="All Centers" />
        <SelectGroup label="Date Range" id="filter-date" value={days} onChange={onDays} options={['CURRENT_MONTH', 'PREVIOUS_MONTH', 'LAST_3_MONTHS', 'PREVIOUS_3_MONTHS', 'CURRENT_WEEK', 'LAST_WEEK', 'YTD', 'FY_YTD', 'CUSTOM', '7', '30', '90', '365', 'ALL']} labels={{ CURRENT_MONTH: 'Current Month', PREVIOUS_MONTH: 'Previous Month', LAST_3_MONTHS: 'Last 3 Months', PREVIOUS_3_MONTHS: 'Previous 3 Months', CURRENT_WEEK: 'Current Week', LAST_WEEK: 'Last Week', YTD: 'YTD (Year to Date)', FY_YTD: 'FY-YTD (Financial Year to Date)', CUSTOM: 'Custom Date', 7: 'Last 7 Days', 30: 'Last 30 Days', 90: 'Last 90 Days', 365: 'Last Year', ALL: 'All Time' }} />
        {days === 'CUSTOM' && <CustomDateInputs from={customFromDate} to={customToDate} onFrom={onCustomFromDate} onTo={onCustomToDate} />}
        <SelectGroup label="Patient Journey" id="dashboard-filter-journey" value={journey} onChange={onJourney} options={['ALL', 'NEW_PATIENT', 'OPU', 'ET']} labels={{ ALL: 'All Journeys', NEW_PATIENT: 'New Patient', OPU: 'Day of OPU', ET: 'Day of ET' }} />
        <button className="btn btn-primary" onClick={onRefresh} id="dash-refresh-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 48, borderRadius: 12, fontWeight: 700 }}>
          <Icon name="refresh" />
          Refresh Data
        </button>
        <button className="btn btn-outline admin-pdf-export-btn" onClick={exportCurrentFilters} disabled={exportDisabled} title={exportDisabled ? 'Dashboard data is still loading.' : 'Export dashboard PDF'} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 48, borderRadius: 12, fontWeight: 700, border: '1px solid var(--primary)', color: 'var(--primary)', background: 'transparent' }}>
          <Icon name="download" />
          Export PDF
        </button>
      </div>
      <div className="filter-meta">
        <span id="dash-last-updated" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <Icon name="clock" />
          {formatLastUpdated(lastUpdated)}
        </span>
        <button type="button" className="auto-refresh-toggle" title="Auto-refresh every 5 minutes" onClick={() => onAutoRefreshChange(!autoRefresh)} style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', fontWeight: 600 }}>
          <span className={`toggle-switch ${autoRefresh ? 'on' : ''}`} id="auto-refresh-toggle" />
          <span>Auto-Refresh (5 min)</span>
        </button>
      </div>
    </div>
  );
}

function CustomDateInputs({ from, to, onFrom, onTo }: { from: string; to: string; onFrom: (value: string) => void; onTo: (value: string) => void }) {
  return (
    <>
      <div className="input-group" style={{ minWidth: 170 }}>
        <label>From Date</label>
        <input type="date" value={from} onChange={event => onFrom(event.target.value)} style={{ height: 48 }} />
      </div>
      <div className="input-group" style={{ minWidth: 170 }}>
        <label>To Date</label>
        <input type="date" value={to} onChange={event => onTo(event.target.value)} style={{ height: 48 }} />
      </div>
    </>
  );
}

export interface DashboardView {
  root: Row;
  total: number;
  nps: number;
  promoters: number;
  passives: number;
  detractors: number;
  responseRate: number;
  promoterRate: number;
  passiveRate: number;
  detractorRate: number;
  centers: ReturnType<typeof centerRows>;
  journeys: ReturnType<typeof journeyRows>;
  staff: ReturnType<typeof staffRows>;
  trend: ReturnType<typeof trendRows>;
  questionCsat: Row[];
  categories: ReturnType<typeof categoryRows>;
  benchmark: BenchmarkRow[];
}

export function buildDashboardView(dashboard: DashboardResponse | null, benchmark: BenchmarkRow[]): DashboardView {
  const stats = dashboardStats(dashboard);
  return {
    ...stats,
    promoterRate: pct(stats.promoters, stats.total),
    passiveRate: pct(stats.passives, stats.total),
    detractorRate: pct(stats.detractors, stats.total),
    centers: centerRows(stats.root),
    journeys: journeyRows(stats.root),
    staff: staffRows(stats.root),
    trend: trendRows(stats.root),
    questionCsat: questionCsatRows(stats.root),
    categories: categoryRows(stats.root),
    benchmark,
  };
}

export function NpsHeroBanner({ view }: { view: DashboardView }) {
 const status = npsStatus(view.nps);
  const clampedNps = Math.max(0, Math.min(100, view.nps));
  const dashOffset = 220 - (220 * clampedNps) / 100;
  const needleRotation = -90 + (180 * clampedNps) / 100;
  return (
    <div className="nps-hero-banner" id="nps-hero-banner">
      <div className="nps-gauge-wrap">
        <svg viewBox="0 0 160 105" xmlns="http://www.w3.org/2000/svg" style={{ overflow: 'visible' }}>
          <path d="M 10 85 A 70 70 0 0 1 150 85" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="10" strokeLinecap="round" />
          <path id="gauge-arc" d="M 10 85 A 70 70 0 0 1 150 85" fill="none" stroke={status.color} strokeWidth="10" strokeLinecap="round" strokeDasharray="220" strokeDashoffset={dashOffset} style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4,0,0.2,1), stroke 0.5s ease' }} />
          <line id="gauge-needle" x1="80" y1="85" x2="80" y2="22" stroke="rgba(255,255,255,0.8)" strokeWidth="2" strokeLinecap="round" style={{ transformOrigin: '80px 85px', transform: `rotate(${needleRotation}deg)`, transition: 'transform 1.2s cubic-bezier(0.4,0,0.2,1)' }} />
          <circle cx="80" cy="85" r="5" fill="white" fillOpacity=".9" />
          <text x="8" y="100" fill="#ffffff" fontSize="7" fontFamily="Outfit" fontWeight="800">0</text>
          <text x="72" y="18" fill="rgba(255,255,255)" fontSize="7" fontFamily="Outfit" fontWeight="800" textAnchor="middle">50</text>
          <text x="148" y="100" fill="#ffffff" fontSize="7" fontFamily="Outfit" fontWeight="800" textAnchor="end">+100</text>
        </svg>
      </div>
      <div className="nps-hero-center">
        <div className="nps-hero-title">Overall Net Promoter Score</div>
        <div className="nps-hero-score-label" id="hero-nps-display">{signed(view.nps)}<span>NPS</span></div>
        <div className="nps-hero-status" id="hero-nps-status"><span id="hero-status-dot" style={{ width: 8, height: 8, borderRadius: '50%', background: status.dot }} />{status.label}</div>
      </div>
      <div className="nps-hero-meta-grid">
        <HeroMeta id="hero-total" value={view.total} label="Responses" />
        <HeroMeta id="hero-promoter-rate" value={`${view.promoterRate}%`} label="Promoter Rate" color="#6ee7b7" />
        <HeroMeta id="hero-detractor-rate" value={`${view.detractorRate}%`} label="Detractor Rate" color="#fca5a5" />
      </div>
    </div>
  );
}

function HeroMeta({ id, value, label, color }: { id: string; value: string | number; label: string; color?: string }) {
  return <div className="nps-hero-meta-item"><div className="nps-hero-meta-val" id={id} style={color ? { color } : undefined}>{value}</div><div className="nps-hero-meta-label">{label}</div></div>;
}

export function OperationalStrategyAdvisor({ view }: { view: DashboardView }) {
  const [page, setPage] = useState(0);
  const bestCenter = [...view.centers].sort((a, b) => b.nps - a.nps)[0];
  const riskCenter = [...view.centers].sort((a, b) => a.nps - b.nps)[0];
  const detractorHeavyCenter = [...view.centers].map(center => ({ ...center, detractorRate: pct(center.detractors, center.total) })).sort((a, b) => b.detractorRate - a.detractorRate)[0];
  const topService = [...view.categories].sort((a, b) => b.score - a.score)[0];
  const focusService = [...view.categories].sort((a, b) => a.score - b.score)[0];
  const lowestJourney = [...view.journeys].filter(item => item.responses > 0).sort((a, b) => a.nps - b.nps)[0];
  const cards: Array<{ title: string; icon: 'trophy' | 'warning' | 'star' | 'wrench' | 'rocket'; color: string; bg: string; wide?: boolean; text: React.ReactNode }> = [];

  if (bestCenter) {
    cards.push({ title: 'Center of Excellence', icon: 'trophy', color: 'var(--success)', bg: '#f0fdf4', text: <><strong>{bestCenter.name} Center</strong> is leading across all locations with an outstanding NPS of <strong>{signed(bestCenter.nps)}</strong>!</> });
  }
  if (riskCenter && riskCenter.nps < 50 && riskCenter.name !== bestCenter?.name) {
    cards.push({ title: 'Priority Attention Required', icon: 'warning', color: 'var(--danger)', bg: '#fef2f2', text: <><strong>{riskCenter.name} Center</strong> has the lowest relative NPS score of <strong>{signed(riskCenter.nps)}</strong>. Operational review suggested.</> });
  } else if (detractorHeavyCenter && detractorHeavyCenter.detractorRate > 15) {
    cards.push({ title: 'Detractor Alert', icon: 'warning', color: 'var(--warning)', bg: '#fffbeb', text: <><strong>{detractorHeavyCenter.name} Center</strong> shows a high detractor ratio of <strong>{detractorHeavyCenter.detractorRate}%</strong>. Investigate patient complaints.</> });
  }
  if (topService) {
    cards.push({ title: 'Key Service Driver', icon: 'star', color: 'var(--primary)', bg: '#fdf2f8', text: <><strong>{topService.name}</strong> scores highest at <strong>{topService.score.toFixed(2)}/5</strong>, driving overall patient satisfaction.</> });
  }
  if (focusService && focusService.score < 4.8) {
    let focusAction = 'Monitor service delivery.';
    if (focusService.name.includes('Staff')) focusAction = 'Schedule staff empathy training and resource planning.';
    else if (focusService.name.includes('Facility')) focusAction = 'Initiate facility cleanliness audit and maintenance.';
    else if (focusService.name.includes('Doctor')) focusAction = 'Review doctor wait-times and scheduling flows.';
    cards.push({ title: 'Operational Focus Area', icon: 'wrench', color: 'var(--warning)', bg: '#fffbeb', text: <><strong>{focusService.name}</strong> rating is currently lagging at <strong>{focusService.score.toFixed(2)}/5</strong>. <em>Action: {focusAction}</em></> });
  }
  if (lowestJourney && lowestJourney.nps < 75) {
    cards.push({ title: 'Journey Flow Spotlight', icon: 'rocket', color: 'var(--accent)', bg: '#f0fdfa', wide: true, text: <>The <strong>{lowestJourney.journey} Journey</strong> exhibits the lowest patient NPS of <strong>{signed(lowestJourney.nps)}</strong>. Patient feedback suggests optimizing center check-in and documentation steps for this cohort.</> });
  }

  if (!cards.length) return null;
  const pages = Math.ceil(cards.length / 2);
  const currentPage = Math.min(page, pages - 1);
  const visibleCards = cards.slice(currentPage * 2, currentPage * 2 + 2);
  return (
    <div className="card" id="ai-insights-panel" style={{ background: 'linear-gradient(135deg, rgba(253, 242, 248, 0.95) 0%, rgba(240, 253, 250, 0.95) 100%)', border: '1px solid rgba(233, 30, 99, 0.15)', marginTop: 32, marginBottom: 0, padding: 24 }}>
      <div className="insights-title"><span style={{ color: 'var(--primary)', display: 'inline-flex' }}><Icon name="sparkles" /></span>Ferty9 Operational Strategy Advisor</div>
      <div className="insights-grid" id="ai-insights-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(240px, 1fr))' }}>
        {visibleCards.map(card => (
          <div className="insight-item" key={card.title} style={card.wide ? { gridColumn: '1 / -1' } : undefined}>
            <div className="insight-icon" style={{ color: card.color, background: card.bg }}><Icon name={card.icon} /></div>
            <div className="insight-content">
              <div className="insight-label">{card.title}</div>
              <div className="insight-text">{card.text}</div>
            </div>
          </div>
        ))}
      </div>
      {pages > 1 && <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
        {Array.from({ length: pages }).map((_, index) => <button key={index} aria-label={`Advisor page ${index + 1}`} className={`btn ${currentPage === index ? 'btn-primary' : 'btn-outline'}`} style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }} onClick={() => setPage(index)}>{index + 1}</button>)}
      </div>}
    </div>
  );
}

export function KpiSection({ view }: { view: DashboardView }) {
  const status = npsStatus(view.nps);
  return (
    <>
      <SectionHeader title="Key Performance Indicators" icon="activity" badge="Live" tone="accent" />
      <div className="kpi-grid">
        <KpiCard id="kpi-total" subId="kpi-total-sub" label="Total Responses" value={view.total} sub={`Response Rate: ${view.responseRate.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}%`} icon="users" iconBg="rgba(13, 148, 136, 0.08)" iconColor="var(--accent)" />
        <KpiCard id="kpi-nps" subId="kpi-nps-sub" label="Net Promoter Score" value={signed(view.nps)} sub={status.label} icon="medal" iconBg="rgba(233, 30, 99, 0.08)" iconColor="var(--primary)" badgeTone={status.tone} />
        <KpiCard id="kpi-promoters" subId="kpi-promoters-sub" label="Promoters" value={view.promoters} sub={`Rate: ${view.promoterRate}% of total`} icon="smile" iconBg="rgba(16, 185, 129, 0.08)" iconColor="var(--success)" borderColor="var(--success)" valueColor="var(--success)" />
        <KpiCard id="kpi-passives" subId="kpi-passives-sub" label="Passives" value={view.passives} sub={`Rate: ${view.passiveRate}% neutral`} icon="meh" iconBg="rgba(245, 158, 11, 0.08)" iconColor="var(--warning)" borderColor="var(--warning)" valueColor="var(--warning)" />
        <KpiCard id="kpi-detractors" subId="kpi-detractors-sub" label="Detractors" value={view.detractors} sub={`Rate: ${view.detractorRate}% ${view.detractorRate > 15 ? 'High' : 'Low'}`} icon="frown" iconBg="rgba(239, 68, 68, 0.08)" iconColor="var(--danger)" borderColor="var(--danger)" valueColor="var(--danger)" />
      </div>
    </>
  );
}

function SectionHeader({ title, icon, badge, tone = 'primary' }: { title: string; icon: 'activity' | 'brain' | 'tag'; badge?: string; tone?: 'primary' | 'accent' }) {
  const color = tone === 'accent' ? 'var(--accent)' : 'var(--primary)';
  const bg = tone === 'accent' ? 'rgba(13,148,136,0.1)' : 'rgba(233,30,99,0.1)';
  return <div className="section-header"><span className="section-header-icon" style={{ background: bg, color }}><Icon name={icon} /></span><span className="section-header-title">{title}</span>{badge && <span className="section-header-badge" style={{ background: bg, color }}>{badge}</span>}</div>;
}

function KpiCard({ id, subId, label, value, sub, icon, iconBg, iconColor, borderColor, valueColor, badgeTone }: { id: string; subId: string; label: string; value: string | number; sub: string; icon: 'users' | 'medal' | 'smile' | 'meh' | 'frown'; iconBg: string; iconColor: string; borderColor?: string; valueColor?: string; badgeTone?: string }) {
  return <div className="kpi-card" style={borderColor ? { borderLeft: `4px solid ${borderColor}` } : undefined}><div><div className="kpi-label">{label}</div><div className="kpi-val kpi-val-animate" id={id} style={valueColor ? { color: valueColor } : undefined}>{value}</div></div><div className="kpi-subtext" id={subId}>{badgeTone ? <span className={`kpi-badge ${badgeTone}`}>{sub}</span> : <span>{sub}</span>}</div><div className="kpi-icon-wrapper" style={{ background: iconBg, color: iconColor }}><Icon name={icon} /></div></div>;
}

export function NpsDistributionBar({ view }: { view: DashboardView }) {
  return (
    <div className="segment-bar-wrap" id="segment-bar-section">
      <div className="segment-bar-labels"><span style={{ color: 'var(--success)' }}>Promoters <span id="seg-promoter-pct" style={{ fontSize: 14 }}>{view.promoterRate}%</span></span><span style={{ color: 'var(--warning)' }}>Passives <span id="seg-passive-pct" style={{ fontSize: 14 }}>{view.passiveRate}%</span></span><span style={{ color: 'var(--danger)' }}>Detractors <span id="seg-detractor-pct" style={{ fontSize: 14 }}>{view.detractorRate}%</span></span></div>
      <div className="segment-bar">
        <div className="segment-bar-fill" id="seg-bar-promoter" style={{ width: `${view.promoterRate}%`, background: GREEN }} />
        <div className="segment-bar-fill" id="seg-bar-passive" style={{ width: `${view.passiveRate}%`, background: AMBER }} />
        <div className="segment-bar-fill" id="seg-bar-detractor" style={{ width: `${view.detractorRate}%`, background: RED }} />
      </div>
      <div className="segment-legend">
        <Legend color={GREEN} label="9-10: Promoters (Loyal Advocates)" />
        <Legend color={AMBER} label="7-8: Passives (Satisfied but Neutral)" />
        <Legend color={RED} label="0-6: Detractors (At-Risk Patients)" />
      </div>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return <span className="segment-legend-item"><span className="segment-dot" style={{ background: color }} />{label}</span>;
}

export function TrendAndDistribution({ view }: { view: DashboardView }) {
  const [metric, setMetric] = useState<'nps' | 'volume' | 'csat'>('nps');
  const metricMeta = {
    nps: { title: 'NPS Trend (Weekly)', label: 'NPS Score', color: PRIMARY, range: { min: -100, max: 100 } },
    volume: { title: 'Feedback Volume (Weekly)', label: 'Responses Count', color: TEAL, range: { min: 0, max: Math.ceil(Math.max(...view.trend.map(item => item.volume), 10) * 1.1) } },
    csat: { title: 'CSAT Trend (Weekly, %)', label: 'CSAT (%)', color: AMBER, range: { min: 0, max: 100 } },
  }[metric];
  const trendConfig = useMemo(() => trendLineConfig(metric, view.trend.map(item => item.label), view.trend.map(item => item[metric]), metricMeta), [metric, metricMeta, view.trend]);
  const distConfig = useMemo(() => doughnutConfig(['Promoters', 'Passives', 'Detractors'], [view.promoters, view.passives, view.detractors], view.nps), [view.detractors, view.nps, view.passives, view.promoters]);
  return (
    <>
      <SectionHeader title="Trend Analysis & Response Distribution" icon="activity" />
      <div className="charts-grid" style={{ marginTop: 0 }}>
        <div className="card trend-card"><div className="trend-card-header"><h3 id="trend-chart-title" style={{ margin: 0 }}>{metricMeta.title}</h3><div id="trend-metric-toggle" className="trend-metric-toggle"><MetricButton id="btn-metric-nps" active={metric === 'nps'} onClick={() => setMetric('nps')}>NPS</MetricButton><MetricButton id="btn-metric-volume" active={metric === 'volume'} onClick={() => setMetric('volume')}>Responses</MetricButton><MetricButton id="btn-metric-csat" active={metric === 'csat'} onClick={() => setMetric('csat')}>CSAT</MetricButton></div></div><ChartBox height={250}><ChartCanvas id="trendChart" config={trendConfig} /></ChartBox></div>
        <div className="card"><h3>Response Distribution</h3><ChartBox height={250}><ChartCanvas id="distChart" config={distConfig} /></ChartBox></div>
      </div>
    </>
  );
}

function MetricButton({ id, active, children, onClick }: { id: string; active: boolean; children: React.ReactNode; onClick: () => void }) {
  return <button id={id} className={`trend-metric-button ${active ? 'active' : ''}`} onClick={onClick}>{children}</button>;
}

export function TrendMetricCard({ view, metric }: { view: DashboardView; metric: 'nps' | 'volume' | 'csat' }) {
  const metricMeta = {
    nps: { title: 'NPS Trend (Weekly)', label: 'NPS Score', color: PRIMARY, range: { min: -100, max: 100 } },
    volume: { title: 'Feedback Volume (Weekly)', label: 'Responses Count', color: TEAL, range: { min: 0, max: Math.ceil(Math.max(...view.trend.map(item => item.volume), 10) * 1.1) } },
    csat: { title: 'CSAT Trend (Weekly, %)', label: 'CSAT (%)', color: AMBER, range: { min: 0, max: 100 } },
  }[metric];
  const trendConfig = useMemo(() => trendLineConfig(metric, view.trend.map(item => item.label), view.trend.map(item => item[metric]), metricMeta), [metric, metricMeta, view.trend]);
  return <div className="card"><h3 style={{ margin: 0, marginBottom: 20 }}>{metricMeta.title}</h3><ChartBox height={250}><ChartCanvas id={`pdf-${metric}-trend-chart`} config={trendConfig} /></ChartBox></div>;
}

export function ResponseDistributionCard({ view }: { view: DashboardView }) {
  const distConfig = useMemo(() => doughnutConfig(['Promoters', 'Passives', 'Detractors'], [view.promoters, view.passives, view.detractors], view.nps), [view.detractors, view.nps, view.passives, view.promoters]);
  return <div className="card"><h3>Response Distribution</h3><ChartBox height={250}><ChartCanvas id="pdf-response-distribution-chart" config={distConfig} /></ChartBox></div>;
}

function ChartBox({ children, height = 300 }: { children: React.ReactNode; height?: number }) {
  return <div style={{ height, position: 'relative' }}>{children}</div>;
}

function chartConfig(type: string, labels: string[], datasets: Row[], yRange?: { min: number; max: number }) {
  return {
    type,
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: type !== 'line', labels: { font: { family: 'Inter', weight: '600', size: 11 } } } },
      scales: type === 'doughnut' || type === 'polarArea' || type === 'radar' ? undefined : { y: { ...yRange, grid: { color: 'rgba(148, 163, 184, 0.06)' }, ticks: { font: { family: 'Outfit', weight: '600' }, color: '#64748b' } }, x: { grid: { display: false }, ticks: { font: { family: 'Outfit', weight: '600' }, color: '#64748b' } } },
    },
  };
}

function trendLineConfig(metric: 'nps' | 'volume' | 'csat', labels: string[], data: number[], meta: { label: string; color: string; range: { min: number; max: number } }) {
  const fill = metric === 'nps' ? 'rgba(233, 30, 99, 0.18)' : metric === 'volume' ? 'rgba(13, 148, 136, 0.18)' : 'rgba(245, 158, 11, 0.18)';
  return {
    type: 'line',
    data: {
      labels,
      datasets: [{
        label: meta.label,
        data,
        borderColor: meta.color,
        borderWidth: 3.5,
        pointBackgroundColor: meta.color,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointHoverRadius: 7,
        pointHoverBorderWidth: 3,
        backgroundColor: fill,
        tension: 0.4,
        fill: true,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        y: {
          ...meta.range,
          grid: { color: 'rgba(148, 163, 184, 0.06)' },
          ticks: { font: { family: 'Outfit', weight: '600' }, color: '#64748b' },
        },
        x: {
          grid: { display: false },
          ticks: { font: { family: 'Outfit', weight: '600' }, color: '#64748b' },
        },
      },
    },
    plugins: [trendGuideLinesPlugin(metric)],
  };
}

function trendGuideLinesPlugin(metric: 'nps' | 'volume' | 'csat') {
  return {
    id: `guideLines-${metric}`,
    afterDraw(chart: { ctx: CanvasRenderingContext2D; scales: { y: { getPixelForValue: (value: number) => number } }; chartArea: { left: number; right: number } }) {
      if (metric !== 'nps' && metric !== 'csat') return;
      const { ctx, scales: { y }, chartArea: { left, right } } = chart;
      ctx.save();
      if (metric === 'nps') {
        drawGuideLine(ctx, y.getPixelForValue(70), left, right, 'rgba(16, 185, 129, 0.4)', [5, 5], 'EXCELLENCE TARGET (70)', 'rgba(16, 185, 129, 0.7)');
        drawGuideLine(ctx, y.getPixelForValue(0), left, right, 'rgba(148, 163, 184, 0.4)', [3, 3], 'NEUTRAL BASELINE (0)', 'rgba(148, 163, 184, 0.7)');
      } else {
        drawGuideLine(ctx, y.getPixelForValue(90), left, right, 'rgba(16, 185, 129, 0.4)', [5, 5], 'EXCELLENCE TARGET (90%)', 'rgba(16, 185, 129, 0.7)');
      }
      ctx.restore();
    },
  };
}

function drawGuideLine(ctx: CanvasRenderingContext2D, y: number, left: number, right: number, stroke: string, dash: number[], label: string, fill: string) {
  ctx.strokeStyle = stroke;
  ctx.lineWidth = dash[0] === 5 ? 1.5 : 1;
  ctx.setLineDash(dash);
  ctx.beginPath();
  ctx.moveTo(left, y);
  ctx.lineTo(right, y);
  ctx.stroke();
  ctx.fillStyle = fill;
  ctx.font = '800 9px Inter';
  ctx.textAlign = 'right';
  ctx.setLineDash([]);
  ctx.fillText(label, right - 10, y - 4);
}

function doughnutConfig(labels: string[], data: number[], npsScore: number) {
  return {
    type: 'doughnut',
    data: { labels, datasets: [{ data, backgroundColor: [GREEN, AMBER, RED], hoverOffset: 4, borderWidth: 0 }] },
    options: { responsive: true, maintainAspectRatio: false, cutout: '76%', plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, boxHeight: 10, usePointStyle: true, pointStyle: 'circle', font: { family: 'Inter', weight: '600', size: 11 } } } } },
    plugins: [{
      id: 'centerText',
      afterDraw(chart: { ctx: CanvasRenderingContext2D; chartArea: { top: number; left: number; width: number; height: number } }) {
        const { ctx, chartArea: { top, left, width, height } } = chart;
        ctx.save();
        ctx.font = '700 36px Outfit';
        ctx.textAlign = 'center';
        ctx.fillStyle = document.body.classList.contains('dark-mode') ? '#f1f5f9' : '#0f172a';
        ctx.textBaseline = 'middle';
        const centerX = left + (width / 2);
        const centerY = top + (height / 2) - 8;
        ctx.fillText(signed(npsScore), centerX, centerY);
        ctx.font = '800 10px Inter';
        ctx.fillStyle = document.body.classList.contains('dark-mode') ? '#94a3b8' : '#64748b';
        ctx.fillText('OVERALL NPS', centerX, centerY + 24);
        ctx.restore();
      },
    }],
  };
}

function horizontalBarConfig(labels: string[], data: number[], label: string, colors: string | string[], range?: { min?: number; max?: number }, showStepSize = false) {
  return {
    type: 'bar',
    data: { labels, datasets: [{ label, data, backgroundColor: colors, borderColor: Array.isArray(colors) ? colors.map(color => color.replace('0.85', '1')) : colors.replace('0.85', '1'), borderWidth: 1.5, borderRadius: 6 }] },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { backgroundColor: '#0f172a', titleFont: { family: 'Outfit', weight: '700' }, bodyFont: { family: 'Inter' }, padding: 12, cornerRadius: 12 } },
      scales: {
        x: { ...range, beginAtZero: range?.min == null, grid: { color: 'rgba(148, 163, 184, 0.06)' }, ticks: { font: { family: 'Outfit', weight: '600' }, color: '#64748b', ...(showStepSize ? { stepSize: 1 } : {}) } },
        y: { grid: { display: false }, ticks: { font: { family: 'Outfit', weight: '600', size: 10 }, color: '#64748b' } },
      },
    },
  };
}

function dynamicNpsColors(values: number[]) {
  return values.map(value => {
    if (value >= 70) return 'rgba(16, 185, 129, 0.85)';
    if (value >= 40) return 'rgba(13, 148, 136, 0.85)';
    if (value >= 10) return 'rgba(245, 158, 11, 0.85)';
    return 'rgba(239, 68, 68, 0.85)';
  });
}

function journeyComparisonConfig(labels: string[], npsData: number[], csatData: number[]) {
  return chartConfig('bar', labels, [
    { label: 'NPS Score', data: npsData, backgroundColor: 'rgba(233, 30, 99, 0.85)', borderColor: PRIMARY, borderWidth: 1.5, borderRadius: 6 },
    { label: 'CSAT (%)', data: csatData, backgroundColor: 'rgba(13, 148, 136, 0.85)', borderColor: TEAL, borderWidth: 1.5, borderRadius: 6 },
  ], { min: 0, max: 100 });
}

function centerStackedConfig(view: DashboardView) {
  return {
    type: 'bar',
    data: {
      labels: view.centers.map(item => item.name),
      datasets: [
        { label: 'Promoter', data: view.centers.map(item => item.promoters), backgroundColor: GREEN, borderRadius: 4 },
        { label: 'Passive', data: view.centers.map(item => item.passives), backgroundColor: AMBER, borderRadius: 4 },
        { label: 'Detractor', data: view.centers.map(item => item.detractors), backgroundColor: RED, borderRadius: 4 },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { backgroundColor: '#0f172a', titleFont: { family: 'Outfit', weight: '700' }, bodyFont: { family: 'Inter' }, padding: 12, cornerRadius: 12 } },
      scales: {
        x: { stacked: true, grid: { display: false }, ticks: { font: { family: 'Outfit', weight: '600', size: 11 }, color: '#64748b', maxRotation: 0, minRotation: 0, autoSkip: false } },
        y: { stacked: true, beginAtZero: true, grid: { color: 'rgba(148, 163, 184, 0.06)' }, ticks: { font: { family: 'Outfit', weight: '600' }, color: '#64748b' } },
      },
    },
  };
}

export function ServiceAndJourneyCharts({ view }: { view: DashboardView }) {
  return (
    <>
      <ServicePerformanceAndJourneyShare view={view} />
      <CenterLeaderboardAndJourneyComparison view={view} />
      <StaffLeaderboardCharts view={view} />
    </>
  );
}

export function ServicePerformanceAndJourneyShare({ view }: { view: DashboardView }) {
  const journeyLabels = view.journeys.map(item => item.journey);
  return (
    <div className="charts-grid" style={{ marginTop: 32, gap: 24 }}>
      <ChartCard title="Service Performance Radar" desc="Comprehensive view of service elements (mapped 0-100) & journey-specific NPS scores." id="radarChart" config={radarConfig(view)} />
      <ChartCard title="Journey Feedback Share" desc="Proportional share of total feedback submissions across distinct patient journey stages." id="journeyVolChart" config={chartConfig('polarArea', journeyLabels, [{ data: view.journeys.map(item => item.responses), backgroundColor: ['rgba(233,30,99,.75)', 'rgba(13,148,136,.75)', 'rgba(245,158,11,.75)'] }])} />
    </div>
  );
}

export function CenterLeaderboardAndJourneyComparison({ view }: { view: DashboardView }) {
  const journeyLabels = view.journeys.map(item => item.journey);
  const centerLeaders = [...view.centers].sort((a, b) => b.nps - a.nps);
  const centerNpsValues = centerLeaders.map(item => item.nps);
  return (
    <div className="charts-grid" style={{ marginTop: 32, gap: 24 }}>
      <ChartCard title="Center NPS Leaderboard" desc="Ranked performance of service locations by calculated Net Promoter Score." id="centerNpsLeaderboardChart" config={horizontalBarConfig(centerLeaders.map(item => item.name), centerNpsValues, 'Center NPS', dynamicNpsColors(centerNpsValues), { min: 0, max: 100 })} />
      <ChartCard title="Journey NPS vs CSAT Comparison" desc="Comparison of Net Promoter Scores and CSAT ratings (%) across journeys." id="journeyComparisonChart" config={journeyComparisonConfig(journeyLabels, view.journeys.map(item => item.nps), view.journeys.map(item => item.csat))} />
    </div>
  );
}

export function StaffLeaderboardCharts({ view }: { view: DashboardView }) {
  const staffVolume = [...view.staff].sort((a, b) => b.count - a.count).slice(0, 10);
  const staffNps = [...view.staff].sort((a, b) => b.nps - a.nps).slice(0, 10);
  const staffNpsValues = staffNps.map(item => item.nps);
  return (
    <div className="charts-grid" style={{ marginTop: 32, gap: 24 }}>
      <ChartCard title="Survey Facilitator Leaderboard (Volume)" desc="Top team members ranked by the number of patient feedback surveys successfully facilitated." id="staffVolLeaderboardChart" config={horizontalBarConfig(staffVolume.map(item => item.name), staffVolume.map(item => item.count), 'Surveys Facilitated', 'rgba(13, 148, 136, 0.85)', undefined, true)} />
      <ChartCard title="Survey Facilitator Performance (NPS)" desc="NPS quality rating score achieved by surveys collected by each team member." id="staffNpsLeaderboardChart" config={horizontalBarConfig(staffNps.map(item => item.name), staffNpsValues, 'NPS Performance', dynamicNpsColors(staffNpsValues), { min: 0, max: 100 })} />
    </div>
  );
}

function ChartCard({ title, desc, id, config }: { title: string; desc: string; id: string; config: Record<string, unknown> }) {
  return <div className="card"><h3>{title}</h3><p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 20 }}>{desc}</p><ChartBox><ChartCanvas id={id} config={config} /></ChartBox></div>;
}

function radarConfig(view: DashboardView) {
  const categoryScore = (name: string) => statNumber(view.categories.find(item => item.name === name) ?? {}, ['score', 'avg', 'avgRating', 'average']);
  const journeyNps = (matches: string[]) => statNumber(view.journeys.find(item => matches.some(match => item.journey.toLowerCase().includes(match))) ?? {}, ['nps', 'score']);
  return {
    type: 'radar',
    data: {
      labels: [
        'Doctor Consultations (%)',
        'Nursing & Staff (%)',
        'Facility Excellence (%)',
        'New Patient NPS',
        'Day of OPU NPS',
        'Day of ET NPS',
      ],
      datasets: [{
        label: 'Excellence Metrics',
        data: [
          categoryScore('Doctor Consultation'),
          categoryScore('Nursing & Staff'),
          categoryScore('Facility & Cleanliness'),
          journeyNps(['new patient']),
          journeyNps(['opu', 'day of opu']),
          journeyNps(['embryo', 'transfer', 'et']),
        ],
        backgroundColor: 'rgba(233, 30, 99, 0.15)',
        borderColor: PRIMARY,
        borderWidth: 3,
        pointBackgroundColor: PRIMARY,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: PRIMARY,
        pointHoverRadius: 6,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        r: {
          angleLines: { color: 'rgba(148, 163, 184, 0.06)' },
          grid: { color: 'rgba(148, 163, 184, 0.30)' },
          pointLabels: { font: { family: 'Outfit', weight: '700', size: 10 }, color: '#64748b' },
          ticks: { backdropColor: 'transparent', color: '#64748b', font: { family: 'Inter', size: 8 } },
          suggestedMin: 0,
          suggestedMax: 100,
        },
      },
    },
  };
}

export function NpsByServiceCentre({ view }: { view: DashboardView }) {
  const config = useMemo(() => centerStackedConfig(view), [view]);
  return <div className="card" style={{ marginTop: 32 }}><div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 24 }}><h3 style={{ margin: 0 }}>NPS by Service Centre</h3><span id="nps-total-n" style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>N={view.total}</span></div><ChartBox height={350}><ChartCanvas id="centerChart" config={config} /></ChartBox><div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 20, fontSize: 12, fontWeight: 600 }}><BlockLegend color={GREEN} label="Promoter" /><BlockLegend color={AMBER} label="Passive" /><BlockLegend color={RED} label="Detractor" /></div></div>;
}

function BlockLegend({ color, label }: { color: string; label: string }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ width: 12, height: 12, background: color, borderRadius: 2 }} />{label}</div>;
}

export function CsatSummaryTables({ view }: { view: DashboardView }) {
  const [selectedJourney, setSelectedJourney] = useState('NEW_PATIENT');
  const categoryRows = view.questionCsat.filter(item => {
    const rowJourney = text(item.journey).toLowerCase();
    if (selectedJourney === 'OPU') return rowJourney.includes('opu');
    if (selectedJourney === 'ET') return rowJourney.includes('et') || rowJourney.includes('embryo');
    return rowJourney.includes('new') || rowJourney.includes('first');
  });
  return (
    <div className="charts-grid" style={{ marginTop: 32, gridTemplateColumns: '1.15fr 0.85fr', gap: 24, alignItems: 'start' }}>
      <div className="card" style={{ padding: 0, overflow: 'hidden', border: '1px solid var(--border)', boxShadow: 'var(--shadow-md)' }}><div style={{ padding: 24, borderBottom: '1px solid var(--border)' }}><h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>CSAT</h3></div><div style={{ overflowX: 'auto' }}><table className="report-table" style={{ marginTop: 0 }}><thead><tr><th style={{ padding: '16px 24px' }}>Journey</th><th style={{ padding: '16px 24px' }}>Response</th><th style={{ padding: '16px 24px' }}>NPS</th><th style={{ padding: '16px 24px' }}>CSAT</th></tr></thead><tbody id="journey-summary-body">{view.journeys.length ? view.journeys.map(item => { const key = journeyKey(item.journey); return <tr key={item.journey} style={{ cursor: 'pointer', background: key === selectedJourney ? 'rgba(233, 30, 99, 0.05)' : undefined }} onClick={() => setSelectedJourney(key)}><td style={{ padding: '16px 24px' }}><strong>{item.journey}</strong></td><td style={{ padding: '16px 24px' }}>{item.responses}</td><td style={{ padding: '16px 24px' }} className={item.nps > 70 ? 'score-high' : 'score-mid'}>{item.nps}</td><td style={{ padding: '16px 24px', fontWeight: 700, color: 'var(--text)' }}>{ratingText(item.avgRating)}</td></tr>; }) : <tr><td colSpan={4}>No journey data found.</td></tr>}</tbody></table></div></div>
      <div className="card" style={{ padding: 0, overflow: 'visible', border: '1px solid var(--border)', boxShadow: 'var(--shadow-md)', position: 'relative', zIndex: 5 }}><div style={{ padding: 24, borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, borderTopLeftRadius: 20, borderTopRightRadius: 20, background: 'var(--surface-solid)' }}><h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>CSAT Category</h3><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>Journey Stage:</span><select id="selected-journey-dropdown" value={selectedJourney} onChange={event => setSelectedJourney(event.target.value)} style={{ padding: '6px 12px', borderRadius: 10, border: '1px solid var(--border)', fontSize: 12, fontWeight: 700, background: 'var(--bg-card)', color: 'var(--text)', outline: 'none', cursor: 'pointer' }}><option value="NEW_PATIENT">New Patients: first visit</option><option value="OPU">Day of OPU (Ovum Pickup)</option><option value="ET">Day of Embryo Transfer</option></select></div></div><div style={{ overflowX: 'auto' }}><table className="report-table" style={{ marginTop: 0 }}><thead><tr><th style={{ padding: '16px 24px' }}>RCA</th><th style={{ padding: '16px 24px' }}>Response</th><th style={{ padding: '16px 24px' }}>CSAT</th></tr></thead><tbody id="category-summary-body">{categoryRows.length ? categoryRows.map((item, index) => { const rating = statNumber(item, ['avgRating', 'average'], statNumber(item, ['csat']) ? statNumber(item, ['csat']) / 20 : 0); return <tr key={`${text(item.category ?? item.question)}-${index}`}><td style={{ padding: '16px 24px' }}><strong>{text(item.category ?? item.question ?? item.rca)}</strong></td><td style={{ padding: '16px 24px' }}>{statNumber(item, ['responses', 'count', 'total'])}</td><td style={{ padding: '16px 24px', fontWeight: 800 }} className={scoreClass(rating)}>{ratingText(rating)}</td></tr>; }) : <tr><td colSpan={3} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No attributes found for the selected journey.</td></tr>}</tbody></table></div></div>
    </div>
  );
}

function scoreClass(score: number) {
  if (score <= 5) {
    if (score > 0 && score < 3.5) return 'score-low';
    if (score > 0 && score < 4.5) return 'score-mid';
    return 'score-high';
  }
  if (score < 10) return 'score-low';
  if (score < 40) return 'score-mid';
  return 'score-high';
}

export function JourneyDetailedMetrics({ view, onExport, exportDisabled = false }: { view: DashboardView; onExport?: () => void; exportDisabled?: boolean }) {
  return (
    <div style={{ marginTop: 32 }}>
      <div className="card" style={{ padding: 0, overflow: 'hidden', border: '1px solid var(--border)', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ padding: 24, borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>Journey Detailed Metrics</h3>{onExport && <button className="btn btn-outline" style={{ padding: '6px 12px', fontSize: 12 }} disabled={exportDisabled} title={exportDisabled ? 'Dashboard export permission is required' : undefined} onClick={onExport}>Export Report</button>}</div>
        <div style={{ overflowX: 'auto' }}><table className="report-table" style={{ marginTop: 0 }}><thead><tr><th style={{ padding: '16px 24px' }}>Journey</th><th style={{ padding: '16px 24px' }}>NPS Score</th><th style={{ padding: '16px 24px' }}>Responses</th><th style={{ padding: '16px 24px', color: 'var(--success)' }}>Promoter</th><th style={{ padding: '16px 24px', color: 'var(--warning)' }}>Passive</th><th style={{ padding: '16px 24px', color: 'var(--danger)' }}>Detractor</th></tr></thead><tbody id="journey-detailed-body">{view.journeys.length ? view.journeys.map(item => <tr key={item.journey}><td>{item.journey}</td><td className={scoreClass(item.nps)}>{signed(item.nps)}</td><td>{item.responses}</td><td>{item.promoters}</td><td>{item.passives}</td><td>{item.detractors}</td></tr>) : <tr><td colSpan={6}>No journey metrics found.</td></tr>}</tbody></table></div>
      </div>
    </div>
  );
}

export function NlpAnalysis({ view }: { view: DashboardView }) {
  const themeAnalysis = record(view.root.themeAnalysis);
  const sentiment = record(themeAnalysis.sentiment);
  const positive = statNumber(sentiment, ['positive'], view.promoters);
  const neutral = statNumber(sentiment, ['neutral'], view.passives);
  const negative = statNumber(sentiment, ['negative'], view.detractors);
  const total = positive + neutral + negative;
  const themes = array(themeAnalysis.themes);
  const keywordRows = array(themeAnalysis.keywords ?? themeAnalysis.words).map((item, index) => ({ word: text(item.word ?? item.keyword ?? item.name), count: statNumber(item, ['count'], 1), index })).filter(item => item.word !== '-');
  const maxKeywordCount = Math.max(...keywordRows.map(item => item.count), 1);
  const maxTheme = Math.max(...themes.map(item => statNumber(item, ['count'])), 1);
  const posPct = pct(positive, total);
  const neuPct = pct(neutral, total);
  const negPct = pct(negative, total);
  return (
    <div className="charts-grid" style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 24, marginTop: 32 }}>
      <div className="card" style={{ padding: 0, overflow: 'hidden', border: '1px solid var(--border)', boxShadow: 'var(--shadow-md)', display: 'flex', flexDirection: 'column' }}><div style={{ padding: 24, borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ color: 'var(--primary)', display: 'inline-flex' }}><Icon name="brain" /></span><h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>Patient Sentiment & Operational Theme Analysis</h3></div><div style={{ padding: 24, flex: 1, display: 'flex', flexDirection: 'column', gap: 24 }}><div><div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13, fontWeight: 700, fontFamily: 'Outfit' }}><span style={{ color: 'var(--text-muted)' }}>Overall Patient Sentiment (NPS Mapped)</span><span id="nlp-sentiment-summary" style={{ color: 'var(--primary)' }}>{sentimentSummary(posPct, negPct, total)}</span></div><div style={{ display: 'flex', height: 16, borderRadius: 8, overflow: 'hidden', background: 'var(--bg)', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)' }}><div id="nlp-sentiment-pos" style={{ width: `${posPct}%`, background: GREEN, transition: 'width 0.5s ease' }} /><div id="nlp-sentiment-neu" style={{ width: `${neuPct}%`, background: AMBER, transition: 'width 0.5s ease' }} /><div id="nlp-sentiment-neg" style={{ width: `${negPct}%`, background: RED, transition: 'width 0.5s ease' }} /></div><div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 12, fontWeight: 700, fontFamily: 'Outfit', color: 'var(--text)', flexWrap: 'wrap' }}><SentimentLegend id="nlp-sentiment-pos-lbl" color={GREEN} label="Positive" value={posPct} /><SentimentLegend id="nlp-sentiment-neu-lbl" color={AMBER} label="Mixed" value={neuPct} /><SentimentLegend id="nlp-sentiment-neg-lbl" color={RED} label="Critical" value={negPct} /></div></div><div><h4 style={{ margin: '0 0 16px', fontFamily: 'Outfit, sans-serif', fontSize: 14, fontWeight: 800, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Mention Frequency by Operational Area</h4><div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} id="nlp-themes-container">{themes.length ? themes.map(item => <ThemeBar key={text(item.name)} name={themeLabel(text(item.name), text(item.label ?? item.name))} color={themeColor(text(item.name))} count={statNumber(item, ['count'])} max={maxTheme} />) : <div style={{ color: 'var(--text-muted)' }}>No theme mentions found.</div>}</div></div></div></div>
      <div className="card" style={{ padding: 0, overflow: 'hidden', border: '1px solid var(--border)', boxShadow: 'var(--shadow-md)', display: 'flex', flexDirection: 'column' }}><div style={{ padding: 24, borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ color: 'var(--accent)', display: 'inline-flex' }}><Icon name="tag" /></span><h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>Trending Feedback Keywords</h3></div><div id="nlp-word-cloud" style={{ padding: 24, flex: 1, display: 'flex', alignContent: 'center', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 10, background: 'var(--surface-solid)' }}>{keywordRows.length ? keywordRows.map(item => <KeywordTag key={`${item.word}-${item.index}`} word={item.word} count={item.count} max={maxKeywordCount} index={item.index} />) : <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-light)', padding: 40, width: '100%' }}>No keywords extracted yet.</div>}</div></div>
    </div>
  );
}

function KeywordTag({ word, count, max, index }: { word: string; count: number; max: number; index: number }) {
  const ratio = count / max;
  const fontSize = 12 + Math.round(ratio * 14);
  const rotate = ((index * 7) % 16) - 8;
  let color = 'var(--text-light)';
  if (ratio > 0.8) color = 'var(--primary)';
  else if (ratio > 0.5) color = 'var(--accent)';
  else if (ratio > 0.3) color = 'var(--text)';
  return <span style={{ fontSize, fontFamily: 'Outfit', fontWeight: 800, color, transform: `rotate(${rotate}deg)`, transition: 'transform 0.2s', cursor: 'default', userSelect: 'none', display: 'inline-block', padding: '4px 8px', borderRadius: 8, background: 'var(--bg)' }} title={`Mentioned ${count} times`}>{word}</span>;
}

function SentimentLegend({ id, color, label, value }: { id: string; color: string; label: string; value: number }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} /><span>{label}:&nbsp;</span><span id={id}>{value}%</span></span>;
}

function sentimentSummary(posPct: number, negPct: number, total: number) {
  if (!total) return 'No Data';
  if (posPct >= 70) return 'Highly Positive Sentiment 🌟';
  if (posPct >= 50) return 'Positive Sentiment 👍';
  if (negPct >= 35) return 'Critical Action Needed ⚠️';
  if (negPct >= 20) return 'Mixed Patient Sentiment';
  return 'Neutral';
}

function themeLabel(name: string, fallback: string) {
  const labels: Record<string, string> = {
    doctor: '🧑‍⚕️ Doctor Consultation & Clinical Empathy',
    staff: '🧑‍⚕️ Nursing, Counselling & Support Staff Care',
    waiting: '⏳ Waiting Time, Crowds & Coordination Delays',
    hygiene: '🏥 Facility Infrastructure, Cleanliness & Bed comfort',
    billing: '💳 Billing, Cost, Pharmacy & Medication Pricing',
  };
  return labels[name] || fallback;
}

function themeColor(name: string) {
  const colors: Record<string, string> = { doctor: 'var(--primary)', staff: 'var(--accent)', waiting: 'var(--warning)', hygiene: '#8b5cf6', billing: '#f43f5e' };
  return colors[name] || 'var(--primary)';
}

function ThemeBar({ name, count, max, color }: { name: string; count: number; max: number; color: string }) {
  return <div><div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 12, fontWeight: 700, fontFamily: 'Outfit', gap: 16 }}><span style={{ color: 'var(--text)' }}>{name}</span><span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{count} mentions</span></div><div style={{ height: 8, borderRadius: 4, background: 'var(--bg)', overflow: 'hidden' }}><div style={{ width: `${pct(count, max)}%`, background: color, height: '100%', borderRadius: 4, transition: 'width 0.5s ease' }} /></div></div>;
}
